import React from "react";
import { Box, Button, Card, CardContent, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FactRow from "./FactRow";
import ProjectMenuButton from "./ProjectMenuButton";
import {
    CARD_CONTENT_SX, cardHeaderSx, CARD_NAME_SX, ENTITY_CARD_SELECTED_SX, ENTITY_CARD_SX,
    CARD_ACTIONS_SX, HEADER_DELETE_SX, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX, STATUS_LABEL_SX,
} from "./entityCardChrome";
import {
    isSelectionFinalized, selectionKind, selectionKindKey, selectionStatusPresentation,
} from "../helper/selectionFlow";
import KindChip from "./KindChip";
import { SELECTION_KIND_ICONS } from "./itemKindStyles";
import { formatDateOnly } from "../helper/formatDate";

/**
 * One selection as the overview lists and the Projects page show it.
 *
 * @param row         a row of api/selection/data (with reproduction_count)
 * @param rights      { allowed, reason } from deleteRights()
 * @param onContinue  Continue / Open (the wizard or the locked overview)
 * @param onOverview  the Overview page
 * @param onDelete    opens the delete dialog
 * @param projects, onMoveToProject, showProject, selected, dragProps  see ExplorationCard
 */
export default function SelectionCard({
    row, rights, onContinue, onOverview, onDelete,
    projects, onMoveToProject, showProject = true, selected = false, dragProps = {},
}) {
    const presentation = selectionStatusPresentation(row.status);
    const sampleCount = Array.isArray(row.selected_samples) ? row.selected_samples.length : 0;
    const finished = isSelectionFinalized(row.status);
    const inUse = row.reproduction_count > 0;
    const kind = selectionKind(row.selection_type);
    const KindIcon = SELECTION_KIND_ICONS[selectionKindKey(row.selection_type)];
    const deleteTitle = inUse
        ? `Used as palette by ${row.reproduction_count} reproduction${row.reproduction_count === 1 ? "" : "s"} — cannot be deleted`
        : rights?.reason || "Delete selection";

    return (
        <Card
            variant="outlined"
            data-kind="selection"
            data-id={row.id}
            sx={{ ...ENTITY_CARD_SX, ...(selected ? ENTITY_CARD_SELECTED_SX : {}) }}
            {...dragProps}
        >
            {/* dark header band: status + actions + name */}
            <Box sx={cardHeaderSx("selection")}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: presentation.color, flexShrink: 0 }} />
                        <Typography variant="body2" sx={STATUS_LABEL_SX} noWrap>
                            {presentation.label}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" sx={{ mr: -0.5, flexShrink: 0 }}>
                        <ProjectMenuButton
                            projects={projects}
                            currentProjectId={row.project || null}
                            onMove={onMoveToProject}
                            itemLabel="selection"
                        />
                        <Tooltip title={deleteTitle}>
                            {/* span keeps the tooltip working on a disabled button */}
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={inUse || !rights?.allowed}
                                    onClick={onDelete}
                                    aria-label="delete selection"
                                    sx={HEADER_DELETE_SX}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>
                <Typography component="div" sx={CARD_NAME_SX}>
                    {row.name}
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                    <KindChip Icon={KindIcon} label={kind.navLabel} title={`${kind.typeLabel}`} />
                </Box>
            </Box>

            <CardContent sx={CARD_CONTENT_SX}>
                <Stack spacing={0} sx={{ flex: 1 }}>
                    <FactRow dense label="Laser device" value={row.laser_device_name || "—"} />
                    <FactRow dense label="Substrate" value={row.material_name || "—"} />
                    <FactRow dense label="Samples" value={String(sampleCount)} />
                    {row.started_by_email ? (
                        <FactRow dense label="Executor" value={row.started_by_email} />
                    ) : null}
                    <FactRow dense label="Created" value={formatDateOnly(row.created_at)} />
                    {showProject ? (
                        <FactRow dense label="Project" value={row.project_name || "—"} />
                    ) : null}

                    <Stack direction="row" spacing={1} alignItems="center" sx={CARD_ACTIONS_SX}>
                        <Button onClick={onContinue} variant="contained" size="small" disableElevation sx={PRIMARY_BUTTON_SX}>
                            {finished ? "Open" : "Continue"}
                        </Button>
                        <Button onClick={onOverview} variant="outlined" size="small" sx={SECONDARY_BUTTON_SX}>
                            Overview
                        </Button>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}
